import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { LABELS } from "~/resources/Labels";
import { SIMPLBreadcrumb } from "../../components/commons/breadcrumb/SimplBreadCrumb";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { SIMPLMenuHeader } from "../../components/commons/header/SIMPLMenuHeader";
import { SIMPLInput } from "../../components/commons/input/SIMPLInput";
import { SIMPLSelectDropdown } from "../../components/commons/input/SIMPLSelectDropdown";
import { SliderField } from "../../components/commons/slider/SIMPLSlider";
import { LoadingWrapper } from "../../components/commons/spiner/SimpleLoaderWrapper";

import { UseVmTemplateForm } from "./UseVmTemplateForm";

import {
  extractCloudProviderList,
  extractComponentsDetails,
  extractEnvDetails,
  getProviders,
} from "~/Utils/Utils";

import { useTranslation } from "react-i18next";
import { FormCloudEnvironment } from "~/models/Commons ";
import { useGetCloudProvisionerFullQuery } from "~/services/cloudProvisioner";
import { useGetVmTemplatesQuery } from "~/services/vm/VmTemplate";
import { VM_CPU_CORE_MAX, VM_STORAGE_MAX_GB } from "~/Utils/Consts";

// -------------------- Types --------------------
interface Prop {
  editedItem: FormCloudEnvironment | null;
}

export const AddVmTemplate = ({ editedItem }: Prop) => {
  const { refetch } = useGetVmTemplatesQuery({
    page: 0,
    size: 999,
    active: false,
  });

  const {
    handleSubmit,
    control,
    errors,
    cloudEnvData,
    cloudProviderName,
    ConfigurationComponentsData,
    PolicyComponentsData,
    ProvissioningComponentsData,
    providerData,
    provisionerData,
    submitForm,
    setValue,
    watch,
    resetField,
    isLoading,
    isError,
    trigger,
  } = UseVmTemplateForm(refetch, undefined!);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [cloudProviderList, setCloudProviderList] = useState<any[]>([]);
  const [cpu, setCpu] = useState<{ min: number; max: number } | null>(null);
  const [ram, setRam] = useState<{ min: number; max: number } | null>(null);
  const [storage, setStorage] = useState<{ min: number; max: number } | null>(
    null,
  );
  const [cloudProvisionerTemplateId, setCloudProvisionerTemplateId] = useState<
    number | undefined
  >(undefined);
  const [osOptions, setOsOptions] = useState<string[]>([]);
  const [selectClouldProvisioner, setSelectClouldProvisioner] = useState<any[]>(
    [],
  );
  const [clouldProvisionerList, setClouldProvisionerList] = useState<any[]>([]);
  const [selectedCloudEnvironments, setSelectedCloudEnvironments] = useState<
    any[]
  >([]);
  const [selectCloudEnv, setSelectCloudEnv] = useState<any[]>([]);
  const [selectProvider, setSelectProvider] = useState<any[]>([]);
  const [selectVmConfiguration, setSelectVmConfiguration] = useState<any[]>([]);
  const [selectPostProvisioning, setSelectPostProvisioning] = useState<any[]>(
    [],
  );
  const [selectPolicy, setSelectPolicy] = useState<any[]>([]);
  const [showOvgInput, setShowOvgInput] = useState<boolean>(false);

  const { data } = useGetCloudProvisionerFullQuery();

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit(submitForm)(event);
  };

  useEffect(() => {
    setCloudProviderList(getProviders(providerData));
  }, [providerData]);

  const getCloudProviderOptions = (
    provider: { cloudProviderName: string; id: number }[],
  ) =>
    provider?.map((cloudEnv) => ({
      label: cloudEnv.cloudProviderName,
      value: cloudEnv.id,
    }));

  const handleCloudProviderChange = (
    value: number,
    onChange: (val: number) => void,
  ) => {
    onChange(value);

    const selected = cloudProviderName.find((p) => p.id === value);
    const isOvh = selected?.cloudProviderName === LABELS.Ovh;

    setShowOvgInput(isOvh);

    resetField("os");

    if (isOvh) {
      resetField("cpuCore");
      resetField("ram");
      resetField("storage");
    }
  };

  const initVmTemplate = () => {
    const provisionerPayload: any = provisionerData?.payload;
    const envPayload = cloudEnvData?.payload as any | undefined;
    const configPayload = ConfigurationComponentsData?.payload as
      | any
      | undefined;
    const postProvisionPayload = ProvissioningComponentsData?.payload as
      | any
      | undefined;
    const policyPayload = PolicyComponentsData?.payload as any | undefined;

    if (provisionerPayload?.content) {
      setClouldProvisionerList(provisionerPayload.content);
      setSelectClouldProvisioner(
        provisionerPayload.content.map(({ id, fileName }: any) => ({
          id,
          fileName,
        })),
      );
    }

    if (envPayload) setSelectCloudEnv(extractEnvDetails(envPayload.content));
    setSelectedCloudEnvironments(envPayload?.content);
    setSelectProvider(extractCloudProviderList(envPayload?.content));

    if (configPayload)
      setSelectVmConfiguration(extractComponentsDetails(configPayload.content));
    if (postProvisionPayload)
      setSelectPostProvisioning(
        extractComponentsDetails(postProvisionPayload.content),
      );
    if (policyPayload)
      setSelectPolicy(extractComponentsDetails(policyPayload.content));
  };

  useEffect(() => {
    initVmTemplate();
  }, [
    data,
    provisionerData,
    cloudEnvData,
    ConfigurationComponentsData,
    PolicyComponentsData,
    ProvissioningComponentsData,
    editedItem,
  ]);

  const provider = watch("providerType");
  const environment = watch("cloudEnvironmentId");
  const selectedId = watch("cloudProvisionerTemplateId");

  // ==================== AUTO FILL OS & SLIDERS WHEN PROVISIONER SELECTED ====================
  // Single source of truth: this effect handles both value pre-fill and slider
  // range state whenever the selected provisioner template changes.
  // The duplicate effect that followed (using getProvisionerValues) has been
  // removed — it ran after this one and could overwrite the correctly set values,
  // and it never triggered onChange so form fields stayed stale when min === max.
  useEffect(() => {
    if (!selectedId?.value) return;

    const provisionerPayload: any = provisionerData?.payload;
    if (!provisionerPayload?.content) return;

    const item = provisionerPayload.content.find(
      (i: any) => i.id === selectedId.value,
    );

    if (!item) return;

    // Update slider min/max ranges first so the SliderField receives correct
    // bounds before the form value is applied.
    // Clamp to backend hard limits (VM_CPU_CORE_MAX, VM_STORAGE_MAX_GB from Consts.ts) so that
    // provisioner templates created before the limits were fixed cannot cause
    // a 400 Validation Failed when the user submits the VM Template form.
    setCpu({
      min: item.minCpuCore ?? 0,
      max: Math.min(item.maxCpuCore ?? 0, VM_CPU_CORE_MAX),
    });
    setRam({ min: item.minRam ?? 0, max: item.maxRam ?? 0 });
    setStorage({
      min: item.minStorage ?? 0,
      max: Math.min(item.maxStorage ?? 0, VM_STORAGE_MAX_GB),
    });
    setOsOptions(item.os ?? []);

    // Pre-fill form fields. shouldDirty ensures react-hook-form treats these
    // as user-provided values so validation passes on submit (critical when
    // min === max and the slider thumb is never dragged by the user).
    setValue("cpuCore", item.minCpuCore, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue("ram", item.minRam, { shouldValidate: true, shouldDirty: true });
    setValue("storage", item.minStorage, {
      shouldValidate: true,
      shouldDirty: true,
    });

    if (item.os?.length > 0) {
      setValue("os", item.os[0], { shouldValidate: true, shouldDirty: true });
    }
  }, [selectedId, provisionerData, setValue]);

  useEffect(() => {
    if (!provider) return;

    const isOvh = provider.label.trim().toLowerCase() === "ovh";
    setShowOvgInput(isOvh);

    if (watch("cloudEnvironmentId") !== null) {
      setValue("cloudEnvironmentId", null);
    }

    if (watch("cloudProvisionerTemplateId") !== null) {
      setValue("cloudProvisionerTemplateId", null);
    }
  }, [provider]);

  useEffect(() => {
    if (!environment) return;

    if (watch("cloudProvisionerTemplateId") !== null) {
      setValue("cloudProvisionerTemplateId", null);
    }
  }, [environment]);

  const filteredEnvironments = provider
    ? (selectedCloudEnvironments?.filter(
        (env) => env.cloudProvider?.cloudProviderName === provider.label,
      ) ?? [])
    : selectCloudEnv;

  const filteredCloudProvisionerTemplate = provider
    ? (clouldProvisionerList?.filter((env) =>
        provider.label.trim().toLowerCase() === "ovh" ? env.isOvh : !env.isOvh,
      ) ?? [])
    : selectClouldProvisioner;

  const watchProvisioner = watch("cloudProvisionerTemplateId");

  useEffect(() => {
    const newValue = watchProvisioner?.value;

    if (cloudProvisionerTemplateId !== newValue) {
      setCloudProvisionerTemplateId(newValue);

      if (watch("os") !== null) {
        setValue("os", null);
      }
    }
  }, [watchProvisioner, cloudProvisionerTemplateId]);

  const handleCancel = () => navigate("/vm-template");

  const validateAtLeastOne = () => {
    const values = watch(["vmconfiguration", "vmPostProviosiing", "vmPolicy"]);
    return values.some(Boolean) || t("MessageSelectAtLeastOneRequired");
  };

  return (
    <SIMPLMenuHeader>
      <LoadingWrapper
        loadingTitle={t("LoadingVmTemplates")}
        isLoading={isLoading}
        isError={isError}
        testId="vmTemplate-loading"
      >
        <div className="flex justify-center bg-white px-6 py-8">
          <div className="w-full max-w-[1104px] rounded-xl border border-[#D6D6D6] bg-white">
            <div className="px-8 pt-6">
              <SIMPLBreadcrumb />
              <h1 className="mt-2 font-['Arial'] text-[28px] font-bold leading-[44px] text-left text-Foundation/Heading normal-case">
                {t("VmTemplate")}
              </h1>
            </div>

            <form onSubmit={handleFormSubmit}>
              <div className="px-8 pb-8 pt-6 space-y-6">
                {/* NAME & DESCRIPTION */}
                <div className="space-y-6 pt-2">
                  <SIMPLInput
                    name="name"
                    control={control}
                    rules={{
                      required: { value: true, message: LABELS.NameRequired },
                    }}
                    messageRequired={t("NameRequired")}
                    label={t("Name")}
                    placeholder={t("Name")}
                    required
                    id="name"
                    fontWeight="bold"
                    background="white"
                    errorLabel={errors.name?.message}
                    testId="vmTemplate-name"
                  />
                </div>

                <div className="space-y-6 pt-2">
                  <SIMPLInput
                    name="description"
                    control={control}
                    label={t("Description")}
                    id="description"
                    fontWeight="bold"
                    background="white"
                    errorLabel={errors.description?.message}
                    placeholder={t("Description")}
                    testId="vmTemplate-description"
                  />
                </div>

                {/* OUTPUT TEMPLATE SELECT */}
                <Controller
                  name="providerType"
                  control={control}
                  render={({ field }) => (
                    <SIMPLSelectDropdown
                      id=""
                      label={t("OutputTemplate")}
                      options={getCloudProviderOptions(cloudProviderList)}
                      value={field.value}
                      onChange={field.onChange}
                      hideErrorLabel={true}
                      placeholder={t("OutputTemplate")}
                      readOnly
                      isRequired={false}
                      background="white"
                      testId="output-template"
                    />
                  )}
                />

                {/* CLOUD PROVIDER SELECT */}
                <div className="space-y-6 pt-2">
                  <Controller
                    name="providerType"
                    control={control}
                    rules={{ required: t("MessageCloudProviderRequired") }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="providerType"
                        label={t("CloudProvider")}
                        options={getCloudProviderOptions(cloudProviderList)}
                        isRequired
                        value={field.value}
                        onChange={(val: any) =>
                          handleCloudProviderChange(val, field.onChange)
                        }
                        hideErrorLabel={false}
                        errorLabel={errors.providerType?.message as string}
                        placeholder={t("CloudProvider")}
                        background="white"
                        testId="vmTemplate-providerType"
                      />
                    )}
                  />
                </div>

                {/* CLOUD ENVIRONMENT SELECT */}
                <div className="space-y-6 pt-2">
                  <Controller
                    name="cloudEnvironmentId"
                    control={control}
                    rules={{
                      required: t("MessageCloudEnvironmentRequired"),
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="cloudEnvironmentId"
                        label={t("CloudEnvironment")}
                        options={filteredEnvironments?.map((cloudEnv) => ({
                          label: cloudEnv.environmentName,
                          value: cloudEnv.id,
                        }))}
                        isRequired
                        isDisabled={!provider}
                        value={field.value}
                        onChange={field.onChange}
                        hideErrorLabel={false}
                        errorLabel={
                          errors.cloudEnvironmentId?.message as string
                        }
                        placeholder={t("CloudEnvironment")}
                        background="white"
                        testId="vmTemplate-cloudenvironmentId"
                      />
                    )}
                  />
                </div>

                {/* CLOUD PROVISIONER TEMPLATE SELECT */}
                <div className="space-y-6 pt-2">
                  <Controller
                    name="cloudProvisionerTemplateId"
                    control={control}
                    rules={{
                      required: t("MessageProvisioningTemplateRequired"),
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="cloudProvisionerTemplateId"
                        label={t("ProvioningTemplate")}
                        options={filteredCloudProvisionerTemplate.map(
                          (template) => ({
                            label: template.fileName,
                            value: template.id,
                          }),
                        )}
                        isRequired
                        isDisabled={!provider}
                        value={field.value}
                        onChange={field.onChange}
                        hideErrorLabel={false}
                        errorLabel={
                          errors.cloudProvisionerTemplateId?.message as string
                        }
                        placeholder={t("ProvioningTemplate")}
                        background="white"
                        testId="vmTemplate-cloudProvisionerTemplateId"
                      />
                    )}
                  />
                </div>

                {/* VM CONFIG / POST PROVISIONING / POLICY */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Controller
                    name="vmconfiguration"
                    control={control}
                    rules={{ validate: validateAtLeastOne }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="vmconfiguration"
                        label={t("VmConfiguration")}
                        options={selectVmConfiguration.map((c) => ({
                          label: c.name,
                          value: c.id,
                        }))}
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          trigger([
                            "vmconfiguration",
                            "vmPostProviosiing",
                            "vmPolicy",
                          ]);
                        }}
                        errorLabel={errors.vmconfiguration?.message as string}
                        placeholder={t("VmConfiguration")}
                        background="white"
                        testId="vmTemplate-vm-configuration"
                      />
                    )}
                  />

                  <Controller
                    name="vmPostProviosiing"
                    control={control}
                    rules={{ validate: validateAtLeastOne }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="vmPostProviosiing"
                        label={t("VmPostProviSioning")}
                        options={selectPostProvisioning.map((p) => ({
                          label: p.name,
                          value: p.id,
                        }))}
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          trigger([
                            "vmconfiguration",
                            "vmPostProviosiing",
                            "vmPolicy",
                          ]);
                        }}
                        errorLabel={errors.vmPostProviosiing?.message as string}
                        placeholder={t("VmPostProviSioning")}
                        background="white"
                        testId="vmTemplate-vm-postProvisioning"
                      />
                    )}
                  />

                  <Controller
                    name="vmPolicy"
                    control={control}
                    rules={{ validate: validateAtLeastOne }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="vmPolicy"
                        label={t("VmPolicy")}
                        options={selectPolicy.map((p) => ({
                          label: p.name,
                          value: p.id,
                        }))}
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          trigger([
                            "vmconfiguration",
                            "vmPostProviosiing",
                            "vmPolicy",
                          ]);
                        }}
                        errorLabel={errors.vmPolicy?.message as string}
                        placeholder={t("VmPolicy")}
                        background="white"
                        testId="vmTemplate-vm-policy"
                      />
                    )}
                  />

                  <Controller
                    name="os"
                    control={control}
                    rules={{ required: t("MessageOsRequired") }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="os"
                        label={t("Os")}
                        options={osOptions?.map((os) => ({
                          label: os,
                          value: os,
                        }))}
                        value={field.value}
                        onChange={field.onChange}
                        hideErrorLabel={false}
                        isRequired
                        errorLabel={errors.os?.message as string}
                        placeholder={t("SelectOs")}
                        background="white"
                        testId="vmTemplate-os"
                      />
                    )}
                  />
                </div>

                {/* SLIDERS */}
                {!showOvgInput && (
                  <>
                    <div className="space-y-6 pt-2">
                      <Controller
                        name="cpuCore"
                        control={control}
                        rules={{
                          required: t("MessageCpuCoreRequired"),
                          validate: (v) =>
                            v <= VM_CPU_CORE_MAX || t("MessageCpuCoreMax"),
                        }}
                        render={({ field, fieldState }) => (
                          <SliderField
                            id="cpuCores"
                            label={t("CpuCore")}
                            min={cpu?.min}
                            max={cpu?.max}
                            value={field.value}
                            valueLabel={t("CpuCoresValue")}
                            onChange={field.onChange}
                            errorLabel={fieldState.error?.message}
                            required
                            testId="vmTemplate-cpuCore"
                          />
                        )}
                      />
                    </div>

                    <div className="space-y-6 pt-2">
                      <Controller
                        name="ram"
                        control={control}
                        rules={{ required: t("MessageRamRequired") }}
                        render={({ field, fieldState }) => (
                          <SliderField
                            id="ram"
                            label={t("Ram")}
                            min={ram?.min}
                            max={ram?.max}
                            value={field.value}
                            valueLabel={`${t("RAM")} (MB)`}
                            onChange={field.onChange}
                            errorLabel={fieldState.error?.message}
                            required
                            testId="vmTemplate-ram"
                          />
                        )}
                      />
                    </div>

                    <div className="space-y-6 pt-2">
                      <Controller
                        name="storage"
                        control={control}
                        rules={{
                          required: t("MessageStorageRequired"),
                          validate: (v) =>
                            v <= VM_STORAGE_MAX_GB || t("MessageStorageMax"),
                        }}
                        render={({ field, fieldState }) => (
                          <SliderField
                            id="storage"
                            label={t("Storage")}
                            min={storage?.min}
                            max={storage?.max}
                            value={field.value}
                            valueLabel={t("StorageValue")}
                            onChange={field.onChange}
                            errorLabel={fieldState.error?.message}
                            required
                            testId="vmTemplate-storage"
                          />
                        )}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* ACTION BUTTONS */}
              <div className="w-full flex justify-end gap-3 bg-[#F5F5F5] border-t border-[#D6D6D6] rounded-b-xl px-6 py-4">
                <SIMPLButtonCustomized
                  label={t("Cancel")}
                  secondary
                  buttonHandlerAction={handleCancel}
                  testId="vmTemplate-button-cancel"
                />
                <SIMPLButtonCustomized
                  label={t("Submit")}
                  primary
                  testId="vmTemplate-button-submit"
                />
              </div>
            </form>
          </div>
        </div>
      </LoadingWrapper>
    </SIMPLMenuHeader>
  );
};
